import { isEqual } from 'lodash';

/**
 * Creates a new map by mapping the values using iteratee.
 * @param map The map to create a new map from.
 * @param iteratee The function to invoke with each value.
 * @returns The new map.
 */
export function mapValues<K, U, V>(map: ReadonlyMap<K, U>, iteratee: (value: U) => V): ReadonlyMap<K, V> {
  const newMap = new Map();

  map.forEach((value, key) => {
    newMap.set(key, iteratee(value));
  });

  return newMap;
}

/**
 * Compares the values of an array and a map.
 * @param map The map to compare.
 * @param array The array to compare.
 * @param keyExtractor The function to extract the key from the array items.
 * @returns True if the array and map values are equal, otherwise false.
 */
export function mapValuesEqual<T, A>(map: ReadonlyMap<string, T>, array: A[], keyExtractor: (item: A) => string, compare: (a: T, b: A) => boolean = isEqual): boolean {
  if (array.length !== map.size) {
    return false;
  }

  for (const item of array) {
    const key = keyExtractor(item);
    if (!map.has(key) || !compare(map.get(key), item)) {
      return false;
    }
  }

  return true;
}
